import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useUploads } from "@/context/UploadContext";
import { AlertTriangle, RotateCcw, X, WifiOff } from "lucide-react";

function useTick(ms = 1000) {
  const [, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT((x) => x + 1), ms); return () => clearInterval(i); }, [ms]);
}

export function retryText(u) {
  if (u.status !== "retrying") return u.error;
  const s = Math.max(0, Math.ceil(((u.retryAt || 0) - Date.now()) / 1000));
  return `${u.error} · automatischer Neuversuch in ${s}s (Versuch ${(u.attempt || 0) + 1})`;
}

/** Sichtbare Liste fehlgeschlagener / wartender Uploads direkt im Posteingang. */
export function UploadIssuesBanner({ companyId }) {
  const { troubled, retry, retryAllFailed, dismiss } = useUploads();
  useTick();
  const rows = troubled.filter((u) => !companyId || u.companyId === companyId);
  if (!rows.length) return null;
  const retrying = rows.filter((u) => u.status === "retrying").length;
  const failed = rows.length - retrying;
  return (
    <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/70 p-3" data-testid="upload-issues-banner">
      <div className="flex flex-wrap items-center gap-2">
        {retrying ? <WifiOff className="h-4 w-4 text-amber-600" /> : <AlertTriangle className="h-4 w-4 text-rose-600" />}
        <p className="text-sm font-semibold text-slate-900" data-testid="upload-issues-title">
          {failed ? `${failed} Upload(s) fehlgeschlagen` : ""}{failed && retrying ? " · " : ""}{retrying ? `${retrying} Upload(s) werden automatisch wiederholt` : ""}
        </p>
        <span className="text-xs text-slate-600">Die Dateien bleiben gespeichert – auch nach Neuladen der Seite.</span>
        <Button size="sm" variant="outline" className="ml-auto h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-100" onClick={retryAllFailed} data-testid="upload-issues-retry-all">
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Alle jetzt erneut versuchen
        </Button>
      </div>
      <ul className="mt-2 divide-y divide-rose-100">
        {rows.map((u) => (
          <li key={u.id} className="flex items-center gap-2 py-1.5" data-testid={`upload-issue-${u.id}`}>
            <span className={`h-2 w-2 rounded-full ${u.status === "retrying" ? "bg-amber-500 animate-pulse" : "bg-rose-500"}`} />
            <span className="text-sm text-slate-900 truncate max-w-[16rem]" title={u.name}>{u.name}</span>
            <span className={`text-xs truncate ${u.status === "retrying" ? "text-amber-700" : "text-rose-700"}`} data-testid={`upload-issue-status-${u.id}`}>{retryText(u)}</span>
            <button type="button" className="ml-auto text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" onClick={() => retry(u.id)} data-testid={`upload-issue-retry-${u.id}`}>
              <RotateCcw className="h-3 w-3" /> jetzt
            </button>
            <button type="button" className="text-slate-400 hover:text-rose-700" title="Verwerfen" onClick={() => dismiss(u.id)} data-testid={`upload-issue-dismiss-${u.id}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
